import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  Event as RouterEvent,
  RouterOutlet,
} from '@angular/router'
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular'
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser'
import { filter, tap } from 'rxjs'
import { Spinner } from './shared/components/spinner/spinner'
import { LoadingService } from './shared/services/loading.service'

function isNavStart(e: RouterEvent): e is NavigationStart {
  return e instanceof NavigationStart
}
function isNavEndLike(e: RouterEvent): e is NavigationEnd | NavigationCancel | NavigationError {
  return e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-root',
  imports: [RouterOutlet, Spinner],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  private router = inject(Router)
  private destroyRef = inject(DestroyRef)
  loadingService = inject(LoadingService)

  protected title = 'ProCart'
  isIframe = false
  loginDisplay = false

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {}

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener

    this.setLoginDisplay()
    this.authService.instance.enableAccountStorageEvents()

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          this.router.navigate(['/logout'])
        } else {
          this.setLoginDisplay()
        }
      })

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.setLoginDisplay()
        this.checkAndSetActiveAccount()
      })

    this.router.events
      .pipe(
        tap((e) => {
          if (isNavStart(e)) this.loadingService.show()
        }),
        tap((e) => {
          if (isNavEndLike(e)) this.loadingService.hide()
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount()

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts()
      this.authService.instance.setActiveAccount(accounts[0])
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest)
    } else {
      this.authService.loginRedirect()
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService
        .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account)
        })
    } else {
      this.authService.loginPopup().subscribe((response: AuthenticationResult) => {
        this.authService.instance.setActiveAccount(response.account)
      })
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/logout',
      })
    } else {
      this.authService.logoutRedirect()
    }
  }
}

